import React, { useState } from 'react';
import { BarChart3, TrendingUp, MapPin, Info } from 'lucide-react';
import { 
  getStatistiquesGlobales, 
  centres,
  typesIntervention 
} from '../../data/mockData';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

function StatistiquesGlobales({ user }) {
  const [periode, setPeriode] = useState('mois');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Fonction pour calculer la période du 27 au 26 du mois suivant
  const getMonthPeriod = (month, year) => {
    // Début: 27 du mois précédent à 00:01
    let debutMonth = month - 1;
    let debutYear = year;
    if (debutMonth < 0) {
      debutMonth = 11;
      debutYear = year - 1;
    }
    const debut = new Date(debutYear, debutMonth, 27);
    
    // Fin: 26 du mois courant à 23:59
    const fin = new Date(year, month, 26);
    
    return {
      debut: debut.toISOString().split('T')[0],
      fin: fin.toISOString().split('T')[0],
      label: format(new Date(year, month, 1), 'MMMM yyyy', { locale: fr })
    };
  };

  const getDateRange = () => {
    const today = new Date();
    let debut, fin, label;

    switch (periode) {
      case 'semaine':
        const firstDay = new Date(today);
        firstDay.setDate(today.getDate() - 7);
        debut = firstDay.toISOString().split('T')[0];
        fin = today.toISOString().split('T')[0];
        label = '7 derniers jours';
        break;
      case 'mois':
        // Utiliser la période du 27 au 26
        const monthPeriod = getMonthPeriod(selectedMonth, selectedYear);
        debut = monthPeriod.debut;
        fin = monthPeriod.fin;
        label = monthPeriod.label;
        break;
      case 'trimestre':
        const firstDayTrimestre = new Date(today);
        firstDayTrimestre.setMonth(today.getMonth() - 3);
        debut = firstDayTrimestre.toISOString().split('T')[0];
        fin = today.toISOString().split('T')[0];
        label = 'Trimestre';
        break;
      case 'personnalise':
        debut = dateDebut;
        fin = dateFin;
        label = 'Période personnalisée';
        break;
      default:
        debut = fin = today.toISOString().split('T')[0];
        label = "Aujourd'hui";
    }

    return { debut, fin, label };
  };

  const months = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const years = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear; y >= currentYear - 2; y--) {
    years.push(y);
  }

  const { debut, fin, label } = getDateRange();
  const stats = getStatistiquesGlobales(debut, fin);

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Statistiques globales</h1>
          <p className="text-muted">Vue d'ensemble de l'activité</p>
        </div>
      </div>

      <div className="stats-filters-row">
        <div className="stats-filters-left">
          <div className="periode-selector">
            <button
              className={`btn ${periode === 'semaine' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setPeriode('semaine')}
            >
              7 derniers jours
            </button>
            <button
              className={`btn ${periode === 'mois' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setPeriode('mois')}
            >
              Mois
            </button>
            <button
              className={`btn ${periode === 'trimestre' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setPeriode('trimestre')}
            >
              Trimestre
            </button>
            <button
              className={`btn ${periode === 'personnalise' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setPeriode('personnalise')}
            >
              Personnalisé
            </button>
          </div>

          {periode === 'mois' && (
            <div className="month-selector">
              <div className="form-group">
                <label>Mois</label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                >
                  {months.map((month, index) => (
                    <option key={index} value={index}>{month}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Année</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                >
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {periode === 'personnalise' && (
            <div className="date-range-selector">
              <div className="form-group">
                <label>Du</label>
                <input
                  type="date"
                  value={dateDebut}
                  onChange={(e) => setDateDebut(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Au</label>
                <input
                  type="date"
                  value={dateFin}
                  onChange={(e) => setDateFin(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Affichage de la période sélectionnée */}
          <div className="periode-display-inline">
            <span>
              {periode === 'mois' ? (
                <>Du 27 {months[(selectedMonth - 1 + 12) % 12].toLowerCase()} au 26 {months[selectedMonth].toLowerCase()} {selectedYear}</>
              ) : (
                <>{label}</>
              )}
            </span>
          </div>
        </div>

        {/* Total des interventions à droite */}
        <div className="stat-card-compact">
          <TrendingUp size={24} className="stat-compact-icon" />
          <div>
            <span className="stat-compact-label">Total interventions</span>
            <span className="stat-compact-value">{stats.nombreTotal}</span>
          </div>
        </div>
      </div>

      <div className="stats-details">
        <div className="stats-card">
          <div className="stats-card-header">
            <MapPin size={24} />
            <h2>Interventions par centre</h2>
          </div>
          <div className="stats-table">
            <table className="table">
              <thead>
                <tr>
                  <th>Centre</th>
                  <th>Ville</th>
                  <th className="text-right">Nombre d'interventions</th>
                </tr>
              </thead>
              <tbody>
                {centres.map(centre => {
                  const nombre = stats.interventionsParCentre[centre.id] || 0;
                  return (
                    <tr key={centre.id}>
                      <td><strong>{centre.nom}</strong></td>
                      <td>{centre.ville}</td>
                      <td className="text-right">
                        <span className="badge badge-primary">{nombre}</span>
                      </td>
                    </tr>
                  );
                })}
                <tr className="table-footer">
                  <td colSpan="2"><strong>Total</strong></td>
                  <td className="text-right">
                    <strong className="badge badge-success">{stats.nombreTotal}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="stats-card">
          <div className="stats-card-header">
            <BarChart3 size={24} />
            <h2>Répartition par type d'intervention</h2>
          </div>
          <div className="stats-table">
            <table className="table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th className="text-right">Nombre</th>
                  <th className="text-right">Pourcentage</th>
                </tr>
              </thead>
              <tbody>
                {stats.nombreTotal === 0 ? (
                  <tr>
                    <td colSpan="3" className="text-center text-muted">
                      Aucune intervention sur cette période
                    </td>
                  </tr>
                ) : (
                  typesIntervention.map(type => {
                    const nombre = stats.repartitionParType[type.code] || 0;
                    const pourcentage = stats.nombreTotal > 0 
                      ? ((nombre / stats.nombreTotal) * 100).toFixed(1)
                      : 0;
                    
                    return (
                      <tr key={type.id}>
                        <td>
                          <span 
                            className="type-badge" 
                            style={{ backgroundColor: type.couleur }}
                          >
                            {type.code}
                          </span>
                          <span className="ml-2">{type.libelle}</span>
                        </td>
                        <td className="text-right">
                          <strong>{nombre}</strong>
                        </td>
                        <td className="text-right">
                          <span className="text-muted">{pourcentage}%</span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StatistiquesGlobales;

